import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ShieldCheck,
} from 'lucide-react'
import { useState, type ReactNode, type SyntheticEvent } from 'react'

import { UF_VALUES, type CompanyDetail } from '@aeris/contracts'
import { formatCnpj } from '@aeris/organization'
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  LoadFailed,
  Select,
  Spinner,
  notify,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import {
  ApiError,
  fetchCancelCode,
  fetchCompany,
  saveCancelCode,
  updateCompany,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * The company's own registration.
 *
 * The screen that was missing, and its absence was not cosmetic. Until this
 * existed, a tenant knew its own razão social and CNPJ and nothing else — a
 * poorer record than any customer in Pessoas, which has had addresses and
 * registrations since the party module shipped.
 *
 * Two things were impossible without it. No fiscal document can be issued:
 * `enderEmit`, `IE` and `CRT` are required by the layout. And no document
 * received can be checked, because deciding whether a supplier's invoice is
 * addressed to this shop means comparing against what the shop is.
 *
 * The banner at the top says what is still missing rather than whether the
 * form is valid — "não dá para emitir" is a dead end, "falta a inscrição
 * estadual" is a task.
 */

const GAP_LABELS: Record<CompanyDetail['issuanceGaps'][number], string> = {
  'state-registration': 'inscrição estadual',
  'tax-regime': 'regime tributário',
  address: 'endereço completo',
  'city-code': 'código IBGE do município',
}

const REGIME_LABELS: Record<'1' | '2' | '3', string> = {
  '1': 'Simples Nacional',
  '2': 'Simples Nacional — excesso de sublimite',
  '3': 'Regime Normal',
}

type Draft = {
  legalName: string
  tradeName: string
  stateRegistration: string
  municipalRegistration: string
  cnae: string
  taxRegime: '' | '1' | '2' | '3'
  street: string
  streetNumber: string
  complement: string
  district: string
  cityCode: string
  city: string
  state: string
  postalCode: string
  phone: string
  countsStock: boolean
  allowNegativeStock: boolean
  usesTill: boolean
  tracksStockDefault: boolean
  reservesOnQuote: boolean
  stockExitAt: 'confirmation' | 'dispatch'
  /** F293: dias de vencido que não travam o fiado. */
  creditOverdueToleranceDays: number
}

const draftFrom = (company: CompanyDetail): Draft => ({
  legalName: company.legalName,
  tradeName: company.tradeName ?? '',
  stateRegistration: company.fiscal.stateRegistration ?? '',
  municipalRegistration: company.fiscal.municipalRegistration ?? '',
  cnae: company.fiscal.cnae ?? '',
  taxRegime: company.fiscal.taxRegime ?? '',
  street: company.fiscal.street ?? '',
  streetNumber: company.fiscal.streetNumber ?? '',
  complement: company.fiscal.complement ?? '',
  district: company.fiscal.district ?? '',
  cityCode: company.fiscal.cityCode ?? '',
  city: company.fiscal.city ?? '',
  state: company.fiscal.state ?? '',
  postalCode: company.fiscal.postalCode ?? '',
  phone: company.fiscal.phone ?? '',
  countsStock: company.salesFlow.countsStock,
  allowNegativeStock: company.salesFlow.allowNegativeStock,
  usesTill: company.salesFlow.usesTill,
  tracksStockDefault: company.salesFlow.tracksStockDefault,
  reservesOnQuote: company.salesFlow.reservesOnQuote,
  stockExitAt: company.salesFlow.stockExitAt,
  creditOverdueToleranceDays: company.salesFlow.creditOverdueToleranceDays,
})

/** Empty means "clear it", which is different from "leave it alone". */
const orNull = (value: string): string | null =>
  value.trim().length === 0 ? null : value.trim()

/**
 * The form, mounted with the company's id as its `key`.
 *
 * That is React's own answer to "reset state when a prop changes", and the
 * reason it matters here: switching company in the topbar must not leave one
 * company's address on screen above another's CNPJ. An effect that copied the
 * prop into state would do the same job a beat later and re-render twice.
 */
const CompanyForm = ({
  company,
  canWrite,
}: {
  company: CompanyDetail
  canWrite: boolean
}): ReactNode => {
  const queryClient = useQueryClient()

  const [draft, setDraft] = useState<Draft>(() => draftFrom(company))
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  /**
   * As três perguntas abaixo do mestre não significam nada com ele desligado.
   *
   * Acrescentado à dica e não substituindo: o texto original continua sendo a
   * explicação do campo, e some no dia em que alguém religar a chave. Um campo
   * cinza sem motivo é a pessoa concluindo que a tela quebrou.
   */
  const whileOff = (hint: string): string =>
    draft.countsStock
      ? hint
      : `${hint} — sem efeito enquanto esta loja não controlar estoque.`

  const save = useMutation({
    mutationFn: async () =>
      await updateCompany(company.id, {
        version: company.version,
        legalName: draft.legalName.trim(),
        tradeName: orNull(draft.tradeName),
        salesFlow: {
          countsStock: draft.countsStock,
          allowNegativeStock: draft.allowNegativeStock,
          usesTill: draft.usesTill,
          /*
           * As outras três vão INTOCADAS mesmo com o mestre desligado.
           *
           * Zerá-las aqui pareceria arrumação e destruiria a promessa do
           * rótulo: religar devolve a loja como ela estava.
           */
          tracksStockDefault: draft.tracksStockDefault,
          reservesOnQuote: draft.reservesOnQuote,
          stockExitAt: draft.stockExitAt,
          creditOverdueToleranceDays: draft.creditOverdueToleranceDays,
        },
        fiscal: {
          stateRegistration: orNull(
            draft.stateRegistration.replace(/[.\-/\s]/g, '')
          ),
          municipalRegistration: orNull(draft.municipalRegistration),
          cnae: orNull(draft.cnae.replace(/\D/g, '')),
          taxRegime: draft.taxRegime === '' ? null : draft.taxRegime,
          street: orNull(draft.street),
          streetNumber: orNull(draft.streetNumber),
          complement: orNull(draft.complement),
          district: orNull(draft.district),
          cityCode: orNull(draft.cityCode.replace(/\D/g, '')),
          city: orNull(draft.city),
          state: orNull(draft.state.toUpperCase()),
          postalCode: orNull(draft.postalCode.replace(/\D/g, '')),
          phone: orNull(draft.phone.replace(/\D/g, '')),
        },
      }),
    onSuccess: async () => {
      setError(null)
      setSaved(true)
      await queryClient.invalidateQueries({ queryKey: ['company'] })
    },
    onError: (cause: unknown) => {
      setSaved(false)
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar. Tente novamente.'
      )
    },
  })

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault()
    setError(null)
    save.mutate()
  }

  const set =
    (key: keyof Draft) =>
    (event: { target: { value: string } }): void => {
      setSaved(false)
      setDraft((current) => ({ ...current, [key]: event.target.value }))
    }

  return (
    <>
      <TabHeader
        title="Empresa"
        description={
          <>
            <span className="font-(--font-mono)">
              {formatCnpj(company.cnpj)}
            </span>
            {' — o CNPJ não é editável: trocá-lo não é uma correção, é outra ' +
              'empresa, e toda nota já emitida carrega o antigo.'}
          </>
        }
      />

      {/* What is still missing before a document could be issued. */}
      {company.issuanceGaps.length === 0 ? (
        <div className="flex items-start gap-2 rounded-(--radius-md) border border-(--success) bg-(--success-subtle) p-3">
          <ShieldCheck
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 text-(--success)"
          />
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            <span className="font-(--weight-semibold) text-(--success)">
              Cadastro fiscal completo.
            </span>{' '}
            Ainda faltam duas coisas que não são dados deste sistema: o
            certificado digital A1 ou A3 e o credenciamento como emissora na
            SEFAZ do seu estado.
          </p>
        </div>
      ) : (
        <div
          role="status"
          className="flex items-start gap-2 rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) p-3"
        >
          <AlertTriangle
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 text-(--warning)"
          />
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            <span className="font-(--weight-semibold) text-(--warning)">
              Ainda não dá para emitir nota:
            </span>{' '}
            falta{' '}
            {company.issuanceGaps.map((gap) => GAP_LABELS[gap]).join(', ')}.
          </p>
        </div>
      )}

      <form onSubmit={submit} className="flex flex-col gap-5">
        <fieldset className="flex flex-col gap-3 border-0 p-0">
          <legend className="text-(length:--text-xs) uppercase tracking-wide text-(--text-muted)">
            Identificação
          </legend>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Razão social">
              {(field) => (
                <Input
                  {...field}
                  value={draft.legalName}
                  onChange={set('legalName')}
                />
              )}
            </Field>
            <Field label="Nome fantasia">
              {(field) => (
                <Input
                  {...field}
                  value={draft.tradeName}
                  onChange={set('tradeName')}
                />
              )}
            </Field>
          </div>
        </fieldset>

        {/*
          As duas perguntas que "dar baixa no estoque" esconde (F034).

          A call com a Elite Digital mostrou que a expressão significa coisas
          diferentes para pessoas diferentes, e que a queixa deles — não poder
          escolher quando a baixa acontece — era na verdade a falta de poder
          responder as duas separadamente. Uma pergunta só não conseguiria
          perguntar isso sem mentir.
        */}
        <fieldset className="flex flex-col gap-3 border-0 p-0">
          <legend className="text-(length:--text-xs) uppercase tracking-wide text-(--text-muted)">
            Estoque nas vendas
          </legend>

          {/*
            F070 — a pergunta que vem antes das outras duas.

            Ela responde SE a loja conta; as de baixo respondem QUANDO a peça
            sai da contagem, e não querem dizer nada para quem não conta. A
            ordem na tela é a ordem em que as perguntas dependem umas das
            outras.

            O rótulo diz "produto novo" e não "esta loja", porque é isso que a
            chave faz. Uma chave que dissesse "esta loja não usa estoque" e
            deixasse 5309 produtos marcados no cadastro seria uma segunda fonte
            de verdade: a tela do produto mostraria "controla" e o balcão
            ignoraria. O catálogo que já existe se muda em Produtos, e a dica
            abaixo é o caminho — dizer só metade aqui é como alguém desliga a
            chave, testa no PDV, vê a mesma recusa e conclui que não funcionou.
          */}
          {/*
            A PRIMEIRA pergunta, porque as outras três só significam alguma
            coisa depois dela (F102).

            A F070 recusou este interruptor para a tela do produto não mentir.
            A objeção é atendida e não evitada: com isto em "Não", a caixa do
            produto aparece desabilitada com a razão, aqui as três perguntas
            abaixo ficam desabilitadas com a razão, e a linha Estoque some do
            menu. Nenhuma tela diz "controla" enquanto o balcão ignora.
          */}
          <Field
            label="Esta loja controla estoque"
            hint="Vale para TODOS os produtos, inclusive os que já existem. Nenhum saldo e nenhum movimento são apagados: religar devolve o número que estava lá e cada produto volta a valer como estava."
          >
            {(field) => (
              <Select
                {...field}
                value={draft.countsStock ? 'yes' : 'no'}
                disabled={!canWrite}
                onChange={(event) => {
                  setDraft((current) => ({
                    ...current,
                    countsStock: event.target.value === 'yes',
                  }))
                }}
              >
                <option value="yes">Sim, esta loja conta estoque</option>
                <option value="no">
                  Não, esta loja não conta estoque de nada
                </option>
              </Select>
            )}
          </Field>

          <Field
            label="Produto novo controla estoque"
            hint={whileOff(
              'Vale para o que você cadastrar daqui em diante. Os produtos que já existem não mudam: para desligar o controle neles, use Produtos, selecione todos e escolha “Deixar de controlar estoque”.'
            )}
          >
            {(field) => (
              <Select
                {...field}
                value={draft.tracksStockDefault ? 'yes' : 'no'}
                disabled={!canWrite || !draft.countsStock}
                onChange={(event) => {
                  setDraft((current) => ({
                    ...current,
                    tracksStockDefault: event.target.value === 'yes',
                  }))
                }}
              >
                {/*
                  O texto mudou na F102, e o RÓTULO do campo não.
                  Dizia "Sim, esta loja conta estoque" — que agora é
                  literalmente a pergunta de cima, e são coisas diferentes:
                  esta é sobre o produto que ainda vai ser cadastrado.
                */}
                <option value="yes">Sim, nasce controlando</option>
                <option value="no">Não, nasce sem controle</option>
              </Select>
            )}
          </Field>

          {/*
            A pergunta que precisa DESAMBIGUAR, e é a condição para a F028 não
            voltar (F103).

            A F028 recusou o negativo porque, para a Elite, ele daria "5309
            saldos afundando em −∞". A objeção caducou quando a chave de cima
            passou a existir — mas caduca de novo se alguém marcar esta achando
            que é aquela. A dica diz, na própria pergunta, que não é.
          */}
          <Field
            label="Vender abaixo de zero"
            hint={whileOff(
              'Para quem conta e vende antes de receber: o saldo fica negativo e aparece em Estoque → A acertar, para ser corrigido depois. NÃO é o jeito de dizer que a loja não conta estoque — essa é a primeira pergunta.'
            )}
          >
            {(field) => (
              <Select
                {...field}
                value={draft.allowNegativeStock ? 'yes' : 'no'}
                disabled={!canWrite || !draft.countsStock}
                onChange={(event) => {
                  setDraft((current) => ({
                    ...current,
                    allowNegativeStock: event.target.value === 'yes',
                  }))
                }}
              >
                <option value="no">Não, recusa quando não tem</option>
                <option value="yes">Sim, deixa o saldo ficar negativo</option>
              </Select>
            )}
          </Field>

          <Field
            label="O orçamento impresso segura a mercadoria"
            hint={whileOff(
              'A peça sai do disponível e continua no estoque físico, então quem contar a prateleira acha o número certo. Ninguém mais consegue vendê-la.'
            )}
          >
            {(field) => (
              <Select
                {...field}
                value={draft.reservesOnQuote ? 'yes' : 'no'}
                disabled={!canWrite || !draft.countsStock}
                onChange={(event) => {
                  setDraft((current) => ({
                    ...current,
                    reservesOnQuote: event.target.value === 'yes',
                  }))
                }}
              >
                <option value="yes">Sim, segura até o cliente retirar</option>
                <option value="no">Não, só garante o preço</option>
              </Select>
            )}
          </Field>

          <Field
            label="A peça sai do estoque físico"
            hint={whileOff(
              'O momento em que a contagem muda. Depois dele, a prateleira e o sistema concordam que a mercadoria não está mais aqui.'
            )}
          >
            {(field) => (
              <Select
                {...field}
                value={draft.stockExitAt}
                disabled={!canWrite || !draft.countsStock}
                onChange={(event) => {
                  setDraft((current) => ({
                    ...current,
                    stockExitAt:
                      event.target.value === 'confirmation'
                        ? 'confirmation'
                        : 'dispatch',
                  }))
                }}
              >
                <option value="dispatch">
                  Quando o cliente leva ou o motoboy sai
                </option>
                <option value="confirmation">
                  Quando o caixa confirma o pedido
                </option>
              </Select>
            )}
          </Field>

          {/*
            F293 — "tem uma nota vencida, eu não consegui colocar no
            crédito". Até estes dias o vencido não trava o fiado; passou
            disso, o gerente libera com a senha dele.
          */}
          <Field
            label="Vencido que ainda deixa vender a prazo"
            hint="Título vencido há até estes dias não trava o fiado. Passou disso, o gerente libera com a senha. Zero trava qualquer vencido."
          >
            {(field) => (
              <Select
                {...field}
                value={String(draft.creditOverdueToleranceDays)}
                disabled={!canWrite}
                onChange={(event) => {
                  setDraft((current) => ({
                    ...current,
                    creditOverdueToleranceDays: Number(event.target.value),
                  }))
                }}
              >
                {[0, 1, 2, 3, 5, 7, 10, 15, 30].map((days) => (
                  <option key={days} value={String(days)}>
                    {days === 0
                      ? 'Nenhum: qualquer vencido trava'
                      : `Até ${String(days)} ${days === 1 ? 'dia' : 'dias'}`}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </fieldset>

        <fieldset className="flex flex-col gap-3 border-0 p-0">
          <legend className="text-(length:--text-xs) uppercase tracking-wide text-(--text-muted)">
            Caixa nos pedidos
          </legend>

          {/*
            F174. Só o pedido: o sinal, a retirada e o estorno ao cancelar. O
            PDV não lê isto — venda de balcão sem caixa aberto seria dinheiro
            sem gaveta para conferir no fim do dia.
          */}
          <Field
            label="O sinal e a retirada passam pelo caixa"
            hint="Desligado, o pagamento do pedido é registrado direto, sem abrir caixa — para a loja que recebe por Pix e maquininha e não tem gaveta. O PDV continua pedindo caixa aberto."
          >
            {(field) => (
              <Select
                {...field}
                value={draft.usesTill ? 'yes' : 'no'}
                disabled={!canWrite}
                onChange={(event) => {
                  setDraft((current) => ({
                    ...current,
                    usesTill: event.target.value === 'yes',
                  }))
                }}
              >
                <option value="yes">Sim, precisa de um caixa aberto</option>
                <option value="no">Não, esta loja não abre caixa</option>
              </Select>
            )}
          </Field>
        </fieldset>

        <fieldset className="flex flex-col gap-3 border-0 p-0">
          <legend className="text-(length:--text-xs) uppercase tracking-wide text-(--text-muted)">
            Fiscal
          </legend>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Inscrição estadual"
              hint="Só dígitos, ou ISENTO. Sem ela não existe NF-e de produto."
            >
              {(field) => (
                <Input
                  {...field}
                  value={draft.stateRegistration}
                  onChange={set('stateRegistration')}
                />
              )}
            </Field>

            <Field
              label="Regime tributário"
              hint="Decide se a nota leva CST ou CSOSN — e o prazo do IBS/CBS."
            >
              {(field) => (
                <Select
                  {...field}
                  value={draft.taxRegime}
                  onChange={set('taxRegime')}
                >
                  <option value="">Não informado</option>
                  {(['1', '2', '3'] as const).map((value) => (
                    <option key={value} value={value}>
                      {REGIME_LABELS[value]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Inscrição municipal"
              hint="Necessária para nota de serviço (ISS)."
            >
              {(field) => (
                <Input
                  {...field}
                  value={draft.municipalRegistration}
                  onChange={set('municipalRegistration')}
                />
              )}
            </Field>

            <Field label="CNAE principal" hint="Sete dígitos, sem pontuação.">
              {(field) => (
                <Input {...field} value={draft.cnae} onChange={set('cnae')} />
              )}
            </Field>
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-3 border-0 p-0">
          <legend className="text-(length:--text-xs) uppercase tracking-wide text-(--text-muted)">
            Endereço
          </legend>

          <div className="grid gap-3 sm:grid-cols-6">
            <Field label="Logradouro" className="sm:col-span-4">
              {(field) => (
                <Input
                  {...field}
                  value={draft.street}
                  onChange={set('street')}
                />
              )}
            </Field>
            <Field label="Número" className="sm:col-span-2">
              {(field) => (
                <Input
                  {...field}
                  value={draft.streetNumber}
                  onChange={set('streetNumber')}
                />
              )}
            </Field>

            <Field label="Complemento" className="sm:col-span-3">
              {(field) => (
                <Input
                  {...field}
                  value={draft.complement}
                  onChange={set('complement')}
                />
              )}
            </Field>
            <Field label="Bairro" className="sm:col-span-3">
              {(field) => (
                <Input
                  {...field}
                  value={draft.district}
                  onChange={set('district')}
                />
              )}
            </Field>

            <Field label="Município" className="sm:col-span-3">
              {(field) => (
                <Input {...field} value={draft.city} onChange={set('city')} />
              )}
            </Field>
            <Field
              label="Código IBGE"
              hint="É o que a SEFAZ valida, não o nome."
              className="sm:col-span-1"
            >
              {(field) => (
                <Input
                  {...field}
                  value={draft.cityCode}
                  onChange={set('cityCode')}
                />
              )}
            </Field>
            {/* A closed set, so a Select — the same UF_VALUES the party
                address already used. This was an Input, which is how one
                concept ended up with two implementations. */}
            <Field label="UF" className="sm:col-span-1">
              {(field) => (
                <Select {...field} value={draft.state} onChange={set('state')}>
                  <option value="">—</option>
                  {UF_VALUES.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="CEP" className="sm:col-span-1">
              {(field) => (
                <Input
                  {...field}
                  value={draft.postalCode}
                  onChange={set('postalCode')}
                />
              )}
            </Field>

            <Field label="Telefone" className="sm:col-span-2">
              {(field) => (
                <Input {...field} value={draft.phone} onChange={set('phone')} />
              )}
            </Field>
          </div>
        </fieldset>

        {error === null ? null : (
          <p
            role="alert"
            className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-sm) text-(--danger)"
          >
            {error}
          </p>
        )}

        <div className="flex items-center justify-end gap-3">
          {saved ? (
            <Badge tone="success">
              <CheckCircle2
                aria-hidden="true"
                className="mr-1 inline h-3 w-3"
              />
              Salvo
            </Badge>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            disabled={!canWrite || save.isPending}
          >
            Salvar
          </Button>
        </div>

        {canWrite ? null : (
          <p className="text-right text-(length:--text-xs) text-(--text-muted)">
            Você pode ver estes dados, mas não alterá-los.
          </p>
        )}
      </form>
    </>
  )
}

/**
 * A senha de cancelamento (F294).
 *
 * O gerente da Elite, 19/10: o caixa cancela todo dia e chamar o gerente a
 * cada pedido não funciona. O dono cria uma senha só para cancelar e passa
 * às caixas — quem cancelou continua registrado, porque é quem está logado.
 */
const CancelCodeSection = (): ReactNode => {
  const queryClient = useQueryClient()
  const status = useQuery({
    queryKey: ['cancel-code'],
    queryFn: async () => await fetchCancelCode(),
  })
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: async (next: string | null) =>
      await saveCancelCode({ code: next }),
    onSuccess: async (_result, next) => {
      setCode('')
      setError(null)
      notify.success(
        next === null
          ? 'Código da loja removido. As autorizações voltam a pedir o gerente.'
          : 'Código da loja salvo. Passe para quem pode autorizar no balcão.'
      )
      await queryClient.invalidateQueries({ queryKey: ['cancel-code'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError ? cause.message : 'Não foi possível salvar.'
      )
    },
  })

  const current = status.data
  const configured = current?.configured === true

  return (
    <form
      className="mt-6 flex flex-col gap-3 border-t border-(--border) pt-6"
      onSubmit={(event) => {
        event.preventDefault()
        if (code.trim().length >= 4) save.mutate(code.trim())
      }}
    >
      <h2 className="text-(length:--text-lg) font-semibold">
        Código da loja
      </h2>
      <p className="text-(length:--text-sm) text-(--text-muted)">
        Um código para o balcão autorizar sem chamar o gerente: cancelar ou
        editar venda, trocas, crédito e o PDV. Fica registrado quem pediu: é
        quem está logado.
        {current?.configured === true && current.setAt !== null
          ? ` Criada em ${new Date(current.setAt).toLocaleString('pt-BR')}${current.setBy === null ? '' : ` por ${current.setBy}`}.`
          : ' A loja ainda não tem.'}
      </p>
      <Field
        label={configured ? 'Trocar a senha' : 'Nova senha'}
        hint="Pelo menos 4 caracteres."
      >
        {(field) => (
          <Input
            {...field}
            type="password"
            autoComplete="new-password"
            value={code}
            onChange={(event) => {
              setCode(event.target.value)
            }}
          />
        )}
      </Field>
      {error === null ? null : (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {error}
        </p>
      )}
      <div className="flex items-center justify-end gap-3">
        {configured ? (
          <Button
            type="button"
            variant="secondary"
            disabled={save.isPending}
            onClick={() => {
              save.mutate(null)
            }}
          >
            Tirar a senha
          </Button>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          disabled={code.trim().length < 4 || save.isPending}
        >
          {configured ? 'Trocar' : 'Criar senha'}
        </Button>
      </div>
    </form>
  )
}

export const EmpresaPage = (): ReactNode => {
  const { session, allows } = useAuth()

  const companyId = session?.context?.companyId ?? null

  const query = useQuery({
    queryKey: ['company', companyId],
    queryFn: ({ signal }) => fetchCompany(companyId ?? '', signal),
    enabled: companyId !== null,
  })

  if (companyId === null) {
    return (
      <EmptyState
        icon={<Building2 aria-hidden="true" className="h-8 w-8" />}
        title="Nenhuma empresa selecionada"
        description="Escolha uma empresa na barra do topo para ver os dados dela."
      />
    )
  }

  /*
   * Não carregou (F132). Antes o `data === undefined` de uma consulta que
   * falhou caía no carregando, e a tela girava para sempre sem dizer nada.
   */
  if (query.isError) {
    return (
      <LoadFailed
        what="os dados da empresa"
        onRetry={() => {
          void query.refetch()
        }}
      />
    )
  }

  if (query.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label="Carregando os dados da empresa" />
      </div>
    )
  }

  return (
    <>
      <CompanyForm
        key={query.data.company.id}
        company={query.data.company}
        canWrite={allows('organization.write')}
      />
      {allows('organization.write') ? <CancelCodeSection /> : null}
    </>
  )
}
